const DEFAULT_API_BASE = 'https://api.cortasks.com';

export function apiBaseUrl() {
  const fromEnv = process.env.REACT_APP_API_URL;
  return (fromEnv && fromEnv.trim()) || DEFAULT_API_BASE;
}

/** @typedef {{ token: string, email: string }} AuthSession */

const AUTH_KEYS = ['coretask_token', 'coretask_user_email'];

/** Clears auth in this tab only (sessionStorage); also removes legacy shared localStorage keys so other tabs stay independent. */
export function clearAuthSession() {
  AUTH_KEYS.forEach((key) => {
    try {
      sessionStorage.removeItem(key);
      localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  });
}

/** Persists sign-in for this browser tab only — use another tab / window to sign in as a different user simultaneously. */
export function setAuthSession({ token, email }) {
  clearAuthSession();
  try {
    sessionStorage.setItem('coretask_token', token);
    sessionStorage.setItem('coretask_user_email', email);
  } catch {
    /* ignore */
  }
}

/** @returns {AuthSession | null} */
export function loadAuthSession() {
  try {
    const token = sessionStorage.getItem('coretask_token');
    const email = sessionStorage.getItem('coretask_user_email');
    if (token && email) return { token, email };
  } catch {
    /* ignore */
  }
  return null;
}

export function getStoredToken() {
  try {
    return sessionStorage.getItem('coretask_token');
  } catch {
    return null;
  }
}

/**
 * @param {string} path Absolute path on the API host, e.g. `/api/me`
 * @param {RequestInit} [options]
 */
export async function authFetch(path, options = {}) {
  const token = getStoredToken();
  if (!token) throw new Error('Not signed in.');
  const relative = path.startsWith('/') ? path : `/${path}`;
  const url = `${apiBaseUrl()}${relative}`;
  const headers = new Headers(options.headers ?? undefined);
  headers.set('Authorization', `Bearer ${token}`);
  if (!headers.has('Accept')) headers.set('Accept', 'application/json');

  let body = options.body;
  if (body !== undefined && body !== null && typeof body !== 'string' && !(body instanceof FormData)) {
    body = JSON.stringify(body);
  }
  if (typeof body === 'string' && !headers.has('Content-Type'))
    headers.set('Content-Type', 'application/json');

  const res = await fetch(url, { ...options, headers, body });

  if (res.status === 401 && !relative.startsWith('/api/auth')) {
    clearAuthSession();
    window.dispatchEvent(new CustomEvent('coretask:session-expired'));
  }

  return res;
}

/**
 * @returns {Promise<{
 *   userId: string|null,
 *   email: string|null,
 *   roles: string[],
 *   canCreateWorkspaceResources: boolean,
 *   ownsAnyProject: boolean,
 * }>}
 */
export async function apiGetMe() {
  const res = await authFetch('/api/me');
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data));
  return {
    userId: data.userId ?? data.UserId ?? null,
    email: data.email ?? data.Email ?? null,
    roles: data.roles ?? data.Roles ?? [],
    canCreateWorkspaceResources: Boolean(data.canCreateWorkspaceResources ?? data.CanCreateWorkspaceResources ?? true),
    ownsAnyProject: Boolean(data.ownsAnyProject ?? data.OwnsAnyProject ?? false),
  };
}

/** @returns {Promise<Array<{ id:number, name:string, description?:string, createdAtUtc:string }>>} normalized loosely */
export async function apiListMyProjects() {
  const res = await authFetch('/api/projects');
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data));
  return Array.isArray(data) ? data : [];
}

export async function apiCreateProject(payload) {
  const res = await authFetch('/api/projects', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data));
  return data;
}

export async function apiAiTaskerCreateProject(payload) {
  const res = await authFetch('/api/ai-tasker/projects', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data));
  return data;
}

export async function apiAiAssistanceSendMessage(payload) {
  const res = await authFetch('/api/ai-assistance/messages', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data));
  return data;
}

/** @param {Record<string, unknown>} patch */
export async function apiUpdateProject(projectId, patch) {
  const res = await authFetch(`/api/projects/${projectId}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data));
  return data;
}

export async function apiDeleteProject(projectId) {
  const res = await authFetch(`/api/projects/${projectId}`, { method: 'DELETE' });
  if (res.status === 204) return;
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data));
}

export async function apiGetProjectBoard(projectId) {
  const res = await authFetch(`/api/projects/${projectId}/board`);
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data));
  return data;
}

export async function apiCreateProjectTask(projectId, payload) {
  const res = await authFetch(`/api/projects/${projectId}/tasks`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data));
  return data;
}

export async function apiMoveProjectTask(projectId, taskId, payload) {
  const res = await authFetch(`/api/projects/${projectId}/tasks/${taskId}/move`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  if (res.status === 204) return;
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data));
}

/** @returns {string} Absolute path fragment for authenticated binary fetch */
export function taskAttachmentContentPath(projectId, taskId, attachmentId) {
  return `/api/projects/${projectId}/tasks/${taskId}/attachments/${attachmentId}/content`;
}

export async function apiGetWorkItemDetail(projectId, taskId) {
  const res = await authFetch(`/api/projects/${projectId}/tasks/${taskId}`);
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data));
  return data;
}

/** @param {{ title: string, description?: string|null, priority: string, columnId: number, dueDateUtc?: string|null, assigneeUserId?: string|null, meetingLink?: string|null }} payload */
export async function apiSaveWorkItem(projectId, taskId, payload) {
  const res = await authFetch(`/api/projects/${projectId}/tasks/${taskId}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  if (res.status === 204) return;
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data));
}

export async function apiUploadTaskAttachment(projectId, taskId, file) {
  const fd = new FormData();
  fd.append('file', file, file.name);
  const res = await authFetch(`/api/projects/${projectId}/tasks/${taskId}/attachments`, {
    method: 'POST',
    body: fd,
    headers: { Accept: 'application/json' },
  });
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data, res.status, res.statusText));
  return data;
}

export async function apiDeleteTaskAttachment(projectId, taskId, attachmentId) {
  const res = await authFetch(
    `/api/projects/${projectId}/tasks/${taskId}/attachments/${encodeURIComponent(String(attachmentId))}`,
    { method: 'DELETE' },
  );
  if (res.status === 204) return;
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data));
}

export async function apiCreateTaskSubtask(projectId, taskId, title) {
  const res = await authFetch(`/api/projects/${projectId}/tasks/${taskId}/subtasks`, {
    method: 'POST',
    body: JSON.stringify({ title }),
  });
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data));
  return data;
}

/** @param {{ title?: string, done?: boolean }} patch */
export async function apiPatchTaskSubtask(projectId, taskId, subtaskId, patch) {
  const res = await authFetch(`/api/projects/${projectId}/tasks/${taskId}/subtasks/${subtaskId}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data));
  return data;
}

export async function apiDeleteTaskSubtask(projectId, taskId, subtaskId) {
  const res = await authFetch(
    `/api/projects/${projectId}/tasks/${taskId}/subtasks/${encodeURIComponent(String(subtaskId))}`,
    { method: 'DELETE' },
  );
  if (res.status === 204) return;
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data));
}

/** @param {{ body: string, parentCommentId?: number|null }} payload */
export async function apiCreateTaskComment(projectId, taskId, payload) {
  const res = await authFetch(`/api/projects/${projectId}/tasks/${taskId}/comments`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data));
  return data;
}

export async function apiPatchTaskComment(projectId, taskId, commentId, body) {
  const res = await authFetch(
    `/api/projects/${projectId}/tasks/${taskId}/comments/${encodeURIComponent(String(commentId))}`,
    { method: 'PATCH', body: JSON.stringify({ body }) },
  );
  if (res.status === 204) return;
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data));
}

export async function apiDeleteTaskComment(projectId, taskId, commentId) {
  const res = await authFetch(
    `/api/projects/${projectId}/tasks/${taskId}/comments/${encodeURIComponent(String(commentId))}`,
    { method: 'DELETE' },
  );
  if (res.status === 204) return;
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data));
}

export async function authFetchBinary(path) {
  const token = getStoredToken();
  if (!token) throw new Error('Not signed in.');
  const relative = path.startsWith('/') ? path : `/${path}`;
  const url = `${apiBaseUrl()}${relative}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}`, Accept: '*/*' },
  });
  if (res.status === 401) {
    clearAuthSession();
    window.dispatchEvent(new CustomEvent('coretask:session-expired'));
  }
  if (!res.ok) throw new Error(`Attachment download failed (${res.status})`);
  return res;
}

export async function fetchTaskAttachmentBlobUrl(projectId, taskId, attachmentId) {
  const res = await authFetchBinary(taskAttachmentContentPath(projectId, taskId, attachmentId));
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

export async function apiListTeamMembers() {
  const res = await authFetch('/api/team-members');
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data));
  return Array.isArray(data) ? data : [];
}

export async function apiCreateTeamMember(payload) {
  const res = await authFetch('/api/team-members', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data));
  return data;
}

export async function apiUpdateTeamMember(memberId, patch) {
  const res = await authFetch(`/api/team-members/${memberId}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data));
  return data;
}

export async function apiDeleteTeamMember(memberId) {
  const res = await authFetch(`/api/team-members/${memberId}`, { method: 'DELETE' });
  if (res.status === 204) return;
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data));
}

export async function apiListMessageThreads() {
  const res = await authFetch('/api/message-threads');
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data));
  const rows = Array.isArray(data) ? data : [];
  console.log('[CoreTask messages] GET /message-threads → %s thread(s)', rows.length, rows);
  return rows;
}

/** @param {Record<string, unknown>} payload kind, participantName?, participantEmail?, title?, memberNames? */
export async function apiCreateMessageThread(payload) {
  const res = await authFetch('/api/message-threads', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data));
  return data;
}

export async function apiGetThreadMessages(threadId) {
  const res = await authFetch(`/api/message-threads/${encodeURIComponent(threadId)}/messages`);
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data));
  const rows = Array.isArray(data) ? data : [];
  // Temporary debug — remove once DM delivery issue is traced
  console.log('[CoreTask messages] GET /message-threads/%s/messages → %s row(s)', threadId, rows.length, rows);
  return rows;
}

/** @param {Record<string, unknown>} payload contentType, body?, linkUrl?, mediaLabel?, parentMessageId? */
export async function apiPostThreadMessage(threadId, payload) {
  const res = await authFetch(`/api/message-threads/${encodeURIComponent(threadId)}/messages`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data));
  return data;
}

/** @param {File} file @param {string} [caption] plain text caption @param {number|string|null} [parentMessageId] */
export async function apiPostThreadMessageAttachment(threadId, file, caption = '', parentMessageId = null) {
  const fd = new FormData();
  fd.append('file', file, file.name);
  if (caption && caption.trim())
    fd.append('caption', caption.trim());
  if (parentMessageId != null && parentMessageId !== '')
    fd.append('parentMessageId', String(parentMessageId));
  const res = await authFetch(`/api/message-threads/${encodeURIComponent(threadId)}/messages/attachment`, {
    method: 'POST',
    body: fd,
    headers: { Accept: 'application/json' },
  });
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data, res.status, res.statusText));
  return data;
}

export function messageAttachmentContentPath(threadId, messageId) {
  return `/api/message-threads/${encodeURIComponent(threadId)}/messages/${encodeURIComponent(String(messageId))}/attachment`;
}

export async function fetchMessageAttachmentBlobUrl(threadId, messageId) {
  const res = await authFetchBinary(messageAttachmentContentPath(threadId, messageId));
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

export async function apiDeleteThreadMessage(threadId, messageId) {
  const res = await authFetch(
    `/api/message-threads/${encodeURIComponent(threadId)}/messages/${encodeURIComponent(messageId)}`,
    { method: 'DELETE' },
  );
  if (res.status === 204) return;
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data));
}

/** Toggle emoji reaction for the current user (add if missing, remove if present). */
export async function apiToggleThreadMessageReaction(threadId, messageId, emoji) {
  const res = await authFetch(
    `/api/message-threads/${encodeURIComponent(threadId)}/messages/${encodeURIComponent(String(messageId))}/reactions`,
    { method: 'POST', body: JSON.stringify({ emoji }) },
  );
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data));
  return data;
}

export async function apiListNotifications() {
  const res = await authFetch('/api/notifications');
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data));
  return Array.isArray(data) ? data : [];
}

export async function apiCreateNotification(payload) {
  const res = await authFetch('/api/notifications', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data));
  return data;
}

export async function apiMarkAllNotificationsRead() {
  const res = await authFetch('/api/notifications/read-all', { method: 'PATCH' });
  if (res.status === 204) return;
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data));
}

export async function apiMarkNotificationRead(notificationId) {
  const res = await authFetch(`/api/notifications/${encodeURIComponent(notificationId)}/read`, {
    method: 'PATCH',
  });
  if (res.status === 204) return;
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data));
}

export async function apiDeleteNotification(notificationId) {
  const res = await authFetch(`/api/notifications/${encodeURIComponent(notificationId)}`, {
    method: 'DELETE',
  });
  if (res.status === 204) return;
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data));
}

export async function apiGetWorkspaceSettings() {
  const res = await authFetch('/api/settings');
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data));
  return data;
}

// ─── Notes ────────────────────────────────────────────────────────────────

export async function apiGetNotes({ search, category, type } = {}) {
  const params = new URLSearchParams();
  if (search)   params.set('search',   search);
  if (category) params.set('category', category);
  if (type)     params.set('type',     type);
  const res  = await authFetch(`/api/notes?${params}`);
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data, res.status, res.statusText));
  return data;
}

export async function apiCreateNote(note) {
  const res  = await authFetch('/api/notes', { method: 'POST', body: JSON.stringify(note) });
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data, res.status, res.statusText));
  return data;
}

export async function apiUpdateNote(id, note) {
  const res  = await authFetch(`/api/notes/${id}`, { method: 'PUT', body: JSON.stringify(note) });
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data, res.status, res.statusText));
  return data;
}

export async function apiDeleteNote(id) {
  const res = await authFetch(`/api/notes/${id}`, { method: 'DELETE' });
  if (!res.ok) { const d = await parseBody(res); throw new Error(formatApiError(d, res.status, res.statusText)); }
}

export async function apiTogglePin(id) {
  const res  = await authFetch(`/api/notes/${id}/pin`, { method: 'POST' });
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data, res.status, res.statusText));
  return data;
}

export async function apiUploadVoiceNote(file, { title, category, duration } = {}) {
  const fd = new FormData();
  fd.append('file',     file, file.name || 'voice-note.webm');
  if (title)    fd.append('title',    title);
  if (category) fd.append('category', category);
  if (duration) fd.append('duration', String(Math.round(duration)));
  const res  = await authFetch('/api/notes/voice', { method: 'POST', body: fd, headers: { Accept: 'application/json' } });
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data, res.status, res.statusText));
  if (data?.audioUrl && data.audioUrl.startsWith('/')) data.audioUrl = apiBaseUrl() + data.audioUrl;
  return data;
}

// ─── Appointments ─────────────────────────────────────────────────────────

export async function apiGetAppointments({ status, from, to, search } = {}) {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  if (from)   params.set('from',   from instanceof Date ? from.toISOString() : from);
  if (to)     params.set('to',     to   instanceof Date ? to.toISOString()   : to);
  if (search) params.set('search', search);
  const res  = await authFetch(`/api/appointments?${params}`);
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data, res.status, res.statusText));
  return data;
}

export async function apiCreateAppointment(appt) {
  const res  = await authFetch('/api/appointments', { method: 'POST', body: JSON.stringify(appt) });
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data, res.status, res.statusText));
  return data;
}

export async function apiUpdateAppointment(id, appt) {
  const res  = await authFetch(`/api/appointments/${id}`, { method: 'PUT', body: JSON.stringify(appt) });
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data, res.status, res.statusText));
  return data;
}

export async function apiDeleteAppointment(id) {
  const res = await authFetch(`/api/appointments/${id}`, { method: 'DELETE' });
  if (!res.ok) { const d = await parseBody(res); throw new Error(formatApiError(d, res.status, res.statusText)); }
}

export async function apiUpdateAppointmentStatus(id, status) {
  const res  = await authFetch(`/api/appointments/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) });
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data, res.status, res.statusText));
  return data;
}

export async function apiGetReminders() {
  const res  = await authFetch('/api/appointments/upcoming-reminders');
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data, res.status, res.statusText));
  return data;
}

// ─── Video ────────────────────────────────────────────────────────────────

/** @param {{ prompt: string, aspectRatio?: string, durationSeconds?: number }} opts */
export async function apiGenerateVideo({ prompt, aspectRatio, durationSeconds }) {
  const res = await authFetch('/api/video/generate', {
    method: 'POST',
    body: JSON.stringify({ prompt, aspectRatio, durationSeconds }),
  });
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data, res.status, res.statusText));
  const url = data.videoUrl ?? data.VideoUrl ?? '';
  return url.startsWith('/') ? apiBaseUrl() + url : url;
}

/** @param {string} text — text to convert to speech, returns an AudioBuffer blob URL */
export async function apiTextToSpeech(text) {
  const res = await authFetch('/api/ai-assistance/tts', {
    method: 'POST',
    body: JSON.stringify({ text }),
  });
  if (!res.ok) {
    const data = await parseBody(res);
    throw new Error(formatApiError(data, res.status, res.statusText));
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

/** @param {File} file — image file (jpeg/png/gif/webp, max 5 MB) */
export async function apiUploadAvatar(file) {
  const fd = new FormData();
  fd.append('file', file, file.name);
  const res = await authFetch('/api/me/avatar', {
    method: 'POST',
    body: fd,
    headers: { Accept: 'application/json' },
  });
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data, res.status, res.statusText));
  if (data?.avatarUrl && data.avatarUrl.startsWith('/')) {
    data.avatarUrl = apiBaseUrl() + data.avatarUrl;
  }
  return data; // { avatarUrl: 'https://api.cortasks.com/avatars/...' }
}

/** @param {Record<string, unknown>} patch */
export async function apiPatchWorkspaceSettings(patch) {
  const res = await authFetch('/api/settings', {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  if (res.status === 204) return;
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data));
}

/** @param {{ currentPassword: string, newPassword: string }} payload */
export async function apiChangePassword(payload) {
  const res = await authFetch('/api/me/change-password', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  if (res.status === 204) return;
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data));
}

export async function apiGetDashboard() {
  const res = await authFetch('/api/dashboard');
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data));
  return data;
}

export function mapDashboardSummary(data) {
  const num = (x, d = 0) => {
    const n = Number(x);
    return Number.isFinite(n) ? n : d;
  };
  const projectsRaw = data.projects ?? data.Projects ?? [];
  const upcomingRaw = data.upcomingTasks ?? data.UpcomingTasks ?? [];
  const weekly = data.weeklyThroughput ?? data.WeeklyThroughput ?? [];
  const activity = data.activityLines ?? data.ActivityLines ?? [];

  return {
    totalProjects: num(data.totalProjects ?? data.TotalProjects),
    openTasks: num(data.openTasks ?? data.OpenTasks),
    completedTasks: num(data.completedTasks ?? data.CompletedTasks),
    teamMemberCount: num(data.teamMemberCount ?? data.TeamMemberCount),
    teamMembersOnline: num(data.teamMembersOnline ?? data.TeamMembersOnline),
    projectsAddedThisMonth: num(data.projectsAddedThisMonth ?? data.ProjectsAddedThisMonth),
    tasksDueSoonCount: num(data.tasksDueSoonCount ?? data.TasksDueSoonCount),
    tasksCompletedToday: num(data.tasksCompletedToday ?? data.TasksCompletedToday),
    projects: Array.isArray(projectsRaw)
      ? projectsRaw.map((p) => ({
          projectId: num(p.projectId ?? p.ProjectId),
          projectNumber: String(p.projectNumber ?? p.ProjectNumber ?? ''),
          name: String(p.name ?? p.Name ?? ''),
          progressPercent: num(p.progressPercent ?? p.ProgressPercent),
        }))
      : [],
    upcomingTasks: Array.isArray(upcomingRaw)
      ? upcomingRaw.map((t) => ({
          taskId: num(t.taskId ?? t.TaskId),
          taskNumber: String(t.taskNumber ?? t.TaskNumber ?? ''),
          projectId: num(t.projectId ?? t.ProjectId),
          projectNumber: String(t.projectNumber ?? t.ProjectNumber ?? ''),
          title: String(t.title ?? t.Title ?? ''),
          projectName: String(t.projectName ?? t.ProjectName ?? ''),
          priority: String(t.priority ?? t.Priority ?? ''),
          dueDateUtc: t.dueDateUtc ?? t.DueDateUtc ?? null,
        }))
      : [],
    weeklyThroughput: Array.isArray(weekly) ? weekly.map((x) => num(x, 0)) : [],
    weeklyThroughputBasedOnCreations: Boolean(
      data.weeklyThroughputBasedOnCreations ?? data.WeeklyThroughputBasedOnCreations ?? false,
    ),
    activityLines: Array.isArray(activity) ? activity.map(String) : [],
  };
}

/** @param {unknown} iso */
export function formatDashboardDueDate(iso) {
  if (iso == null || iso === '') return '—';
  const d = new Date(String(iso));
  if (Number.isNaN(d.getTime())) return '—';
  const now = new Date();
  const dayStart = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((dayStart(d) - dayStart(now)) / 86400000);
  const time = `${d.getHours() % 12 || 12}:${d.getMinutes().toString().padStart(2, '0')} ${
    d.getHours() < 12 ? 'AM' : 'PM'
  }`;
  if (diff === 0) return `Today · ${time}`;
  if (diff === 1) return `Tomorrow · ${time}`;
  if (diff === -1) return `Yesterday · ${time}`;
  return `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} · ${time}`;
}

/** Normalize API `/api/settings` payload into Settings page shape */
export function mapWorkspaceSettingsResponse(data) {
  const email = String(data.email ?? data.Email ?? '');
  const profileRaw = data.profile ?? data.Profile ?? {};
  const notifRaw = data.notificationPrefs ?? data.NotificationPrefs ?? {};
  const appearanceRaw = data.appearance ?? data.Appearance ?? {};
  const workspaceRaw = data.workspace ?? data.Workspace ?? {};
  const securityRaw = data.security ?? data.Security ?? {};
  const integrationsRaw = data.integrations ?? data.Integrations;

  const channel = (blob) => ({
    email: Boolean(blob?.email ?? blob?.Email ?? false),
    inApp: Boolean(blob?.inApp ?? blob?.InApp ?? false),
    push: Boolean(blob?.push ?? blob?.Push ?? false),
  });

  const notifPick = (camelKey, pascalKey) =>
    channel(notifRaw[camelKey] ?? notifRaw[pascalKey] ?? {});

  return {
    email,
    profile: {
      name: String(profileRaw.displayName ?? profileRaw.DisplayName ?? ''),
      email,
      bio: String(profileRaw.bio ?? profileRaw.Bio ?? ''),
      timezone: String(profileRaw.timezone ?? profileRaw.Timezone ?? 'UTC'),
      avatarUrl: (() => {
        const raw = (profileRaw.avatarUrl ?? profileRaw.AvatarUrl ?? null) || null;
        if (!raw) return null;
        return raw.startsWith('/') ? apiBaseUrl() + raw : raw;
      })(),
    },
    notificationPrefs: {
      taskAssigned: notifPick('taskAssigned', 'TaskAssigned'),
      commentMentioned: notifPick('commentMentioned', 'CommentMentioned'),
      dueDateApproaching: notifPick('dueDateApproaching', 'DueDateApproaching'),
      meetingLinkShared: notifPick('meetingLinkShared', 'MeetingLinkShared'),
    },
    appearance: {
      mode: String(appearanceRaw.mode ?? appearanceRaw.Mode ?? 'System'),
      accent: String(appearanceRaw.accent ?? appearanceRaw.Accent ?? '#1A5FB4'),
      fontSize: String(appearanceRaw.fontSize ?? appearanceRaw.FontSize ?? 'Medium'),
      compactSidebar: Boolean(
        appearanceRaw.compactSidebar ?? appearanceRaw.CompactSidebar ?? false,
      ),
    },
    workspace: {
      name: String(workspaceRaw.name ?? workspaceRaw.Name ?? ''),
      invitation: String(workspaceRaw.invitation ?? workspaceRaw.Invitation ?? ''),
      defaultProjectPermission: String(
        workspaceRaw.defaultProjectPermission ?? workspaceRaw.DefaultProjectPermission ?? '',
      ),
    },
    integrations: Array.isArray(integrationsRaw)
      ? integrationsRaw.map((row) => ({
          id: String(row.id ?? row.Id ?? ''),
          name: String(row.name ?? row.Name ?? ''),
          connected: Boolean(row.connected ?? row.Connected ?? false),
        }))
      : [],
    security: {
      twoFactor: Boolean(
        securityRaw.twoFactorEnabled ?? securityRaw.TwoFactorEnabled ?? false,
      ),
    },
  };
}

/** @returns {Record<string, unknown>} */
export function mapTeamMemberDtoToUi(row) {
  const id = row.id ?? row.Id;
  const projectIdsRaw = row.projectIds ?? row.ProjectIds;
  const projectIds = Array.isArray(projectIdsRaw) ? projectIdsRaw.map((x) => Number(x)).filter(Number.isFinite) : [];
  const assignedRaw = row.assignedTasks ?? row.AssignedTasks;
  const assignedTasks = Array.isArray(assignedRaw) ? assignedRaw.map(String) : [];
  const activityRaw = row.activity ?? row.Activity;
  const activity =
    Array.isArray(activityRaw) && activityRaw.length ? activityRaw.map((n) => Number(n) || 0) : [0, 0, 0, 0, 0];

  let joinedUtc = row.joinedUtc ?? row.JoinedUtc;
  if (typeof joinedUtc === 'string')
    joinedUtc = joinedUtc.slice(0, 10);
  else if (joinedUtc && typeof joinedUtc === 'object' && joinedUtc instanceof Date)
    joinedUtc = joinedUtc.toISOString().slice(0, 10);
  else if (joinedUtc)
    joinedUtc = String(joinedUtc).slice(0, 10);

  const memberUserIdRaw = row.memberUserId ?? row.MemberUserId;
  const memberUserId =
    memberUserIdRaw === null || memberUserIdRaw === undefined ? '' : String(memberUserIdRaw).trim();

  return {
    id: String(id),
    name: String(row.name ?? row.Name ?? ''),
    email: String(row.email ?? row.Email ?? ''),
    memberUserId,
    role: String(row.role ?? row.Role ?? 'Viewer'),
    online: Boolean(row.online ?? row.Online ?? false),
    activeProjects: Number(row.activeProjects ?? row.ActiveProjects ?? projectIds.length) || 0,
    taskCount: Number(row.taskCount ?? row.TaskCount ?? 0) || 0,
    joinedDate: joinedUtc || '',
    assignedTasks,
    activity,
    projectIds,
  };
}

/** @param {Record<string, unknown>} row */
export function mapMessageThreadSummaryDto(row) {
  const id = row.id ?? row.Id;
  const memberNamesRaw = row.memberNames ?? row.MemberNames;
  const members = Array.isArray(memberNamesRaw)
    ? memberNamesRaw.filter((x) => typeof x === 'string').map((x) => String(x))
    : [];
  const lastUtc = row.lastActivityUtc ?? row.LastActivityUtc;
  return {
    id: Number(id),
    threadKind: String(row.threadKind ?? row.ThreadKind ?? 'direct').toLowerCase(),
    name: String(row.name ?? row.Name ?? ''),
    avatar: String(row.avatar ?? row.Avatar ?? '??'),
    online: Boolean(row.online ?? row.Online ?? false),
    previewText: String(row.previewText ?? row.PreviewText ?? ''),
    lastActivityUtc: typeof lastUtc === 'string' ? lastUtc : lastUtc ? String(lastUtc) : '',
    unreadCount: Number(row.unreadCount ?? row.UnreadCount ?? 0) || 0,
    memberNames: members,
  };
}

/** @param {Record<string, unknown>} row @param {string} peerName */
export function mapChatMessageDto(row, peerName) {
  const id = row.id ?? row.Id;
  const sentByOwner = Boolean(row.sentByOwner ?? row.SentByOwner ?? false);
  const fromCurrentUserRaw = row.fromCurrentUser ?? row.FromCurrentUser;
  const own =
    typeof fromCurrentUserRaw === 'boolean' ? fromCurrentUserRaw : sentByOwner;
  const sentUtc = row.sentUtc ?? row.SentUtc;
  const iso = typeof sentUtc === 'string' ? sentUtc : sentUtc ? String(sentUtc) : '';
  const contentType = String(row.contentType ?? row.ContentType ?? 'text').toLowerCase();
  const peerSender = String(row.peerSenderName ?? row.PeerSenderName ?? '').trim();
  const hasAttachmentBlob = Boolean(row.hasAttachmentBlob ?? row.HasAttachmentBlob ?? false);
  const attachmentOriginalFileName = row.attachmentOriginalFileName ?? row.AttachmentOriginalFileName ?? '';
  const attachmentKind = row.attachmentKind ?? row.AttachmentKind ?? '';
  const attachmentContentType = row.attachmentContentType ?? row.AttachmentContentType ?? '';
  const attachmentFileLength = row.attachmentFileLength ?? row.AttachmentFileLength;

  const ts = formatMessageBubbleTime(iso);
  const reactionsRaw = row.reactions ?? row.Reactions;
  const reactions = Array.isArray(reactionsRaw)
    ? reactionsRaw.map((r) => ({
        emoji: String(r.emoji ?? r.Emoji ?? ''),
        count: Number(r.count ?? r.Count ?? 0) || 0,
        includesMe: !!(r.includesMe ?? r.IncludesMe),
      }))
    : [];
  const parentRaw = row.parentMessageId ?? row.ParentMessageId;

  return {
    id: String(id),
    sender: own ? 'You' : peerSender || peerName || 'Member',
    own,
    type: contentType,
    text: String(row.body ?? row.Body ?? ''),
    link: row.linkUrl ?? row.LinkUrl ?? undefined,
    mediaLabel: row.mediaLabel ?? row.MediaLabel ?? undefined,
    timestamp: ts,
    read: own,
    hasAttachmentBlob,
    parentMessageId:
      parentRaw != null && parentRaw !== '' && Number.isFinite(Number(parentRaw)) ? String(parentRaw) : null,
    reactions,
    attachmentOriginalFileName: attachmentOriginalFileName ? String(attachmentOriginalFileName) : undefined,
    attachmentKind: attachmentKind ? String(attachmentKind).toLowerCase() : undefined,
    attachmentContentType: attachmentContentType ? String(attachmentContentType) : undefined,
    attachmentFileLength:
      attachmentFileLength != null && Number.isFinite(Number(attachmentFileLength))
        ? Number(attachmentFileLength)
        : undefined,
  };
}

/** @param {string} isoUtc */
export function formatThreadSidebarTime(isoUtc) {
  if (!isoUtc) return '';
  const d = new Date(isoUtc);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfD = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dayDiff = Math.round((startOfToday.getTime() - startOfD.getTime()) / 86400000);
  if (dayDiff <= 0) {
    let h = d.getHours();
    const am = h < 12;
    const m = d.getMinutes();
    const h12 = h % 12 || 12;
    return `${h12}:${m.toString().padStart(2, '0')} ${am ? 'AM' : 'PM'}`;
  }
  if (dayDiff === 1) return 'Yesterday';
  if (dayDiff < 7)
    return d.toLocaleDateString(undefined, { weekday: 'short' });
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** @param {string} isoUtc */
function formatMessageBubbleTime(isoUtc) {
  if (!isoUtc) return '';
  const d = new Date(isoUtc);
  if (Number.isNaN(d.getTime())) return '';
  let h = d.getHours();
  const am = h < 12;
  const m = d.getMinutes();
  const h12 = h % 12 || 12;
  return `${h12}:${m.toString().padStart(2, '0')} ${am ? 'AM' : 'PM'}`;
}

/** @param {string} isoUtc */
export function formatNotificationRelativeTime(isoUtc) {
  if (!isoUtc) return '';
  const d = new Date(isoUtc);
  if (Number.isNaN(d.getTime())) return '';
  const now = Date.now();
  const diffMs = now - d.getTime();
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return 'Just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const dayStart = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.floor((dayStart(new Date()) - dayStart(d)) / 86400000);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** @param {string} isoUtc @returns {'Today'|'Yesterday'|'This Week'} */
export function notificationTimeGroup(isoUtc) {
  if (!isoUtc) return 'This Week';
  const d = new Date(isoUtc);
  if (Number.isNaN(d.getTime())) return 'This Week';
  const dayStart = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const now = new Date();
  const diffDays = Math.round((dayStart(now) - dayStart(d)) / 86400000);
  if (diffDays <= 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  return 'This Week';
}

/** @param {Record<string, unknown>} row */
export function mapNotificationDto(row) {
  const id = row.id ?? row.Id;
  const created = row.createdUtc ?? row.CreatedUtc;
  const iso = typeof created === 'string' ? created : created ? String(created) : '';
  const isRead = Boolean(row.isRead ?? row.IsRead ?? false);
  return {
    id: String(id),
    type: String(row.type ?? row.Type ?? 'Task Assigned'),
    text: String(row.text ?? row.Text ?? ''),
    linkTarget: String(row.linkTarget ?? row.LinkTarget ?? 'projects'),
    unread: !isRead,
    timestamp: formatNotificationRelativeTime(iso),
    group: notificationTimeGroup(iso),
    createdUtc: iso,
  };
}

/** @param {unknown} errors */
function collectValidationMessages(errors) {
  if (!errors || typeof errors !== 'object') return [];
  return Object.values(errors).flatMap((entry) => {
    if (Array.isArray(entry)) return entry.filter((x) => typeof x === 'string');
    if (typeof entry === 'string') return [entry];
    return [];
  });
}

/**
 * @param {unknown} body
 * @param {number} [httpStatus]
 * @param {string} [statusText]
 */
function formatApiError(body, httpStatus, statusText) {
  if (body && typeof body === 'object') {
    if ('error' in body && typeof body.error === 'string') return body.error;
    if ('detail' in body && typeof body.detail === 'string') return body.detail;
    const fromErrors = collectValidationMessages('errors' in body ? body.errors : null);
    if (fromErrors.length) return fromErrors.join(' ');
    if ('title' in body && typeof body.title === 'string') return body.title;
    if ('errors' in body && body.errors && typeof body.errors === 'object') {
      const firstEntry = Object.values(body.errors)[0];
      if (Array.isArray(firstEntry) && firstEntry[0]) return String(firstEntry[0]);
    }
  }

  const st =
    typeof httpStatus === 'number'
      ? `${httpStatus}${statusText && String(statusText).trim() ? ` ${String(statusText).trim()}` : ''}`
      : '';
  if (httpStatus === 413) return 'Upload is too large for the server. Try a smaller file (max 50 MB).';
  if (httpStatus === 401 || httpStatus === 403) return `Not authorized${st ? ` (${st})` : ''}.`;
  if (httpStatus && httpStatus >= 500)
    return st
      ? `Server error (${st}).`
      : 'Server error. If you are developing the API, check the console / exception page.';
  return st ? `Request failed (${st}).` : 'Request failed.';
}

async function parseBody(res) {
  const text = await res.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return { error: text };
  }
}

/** @param {{ email: string, password: string }} creds */
export async function apiLogin({ email, password }) {
  const res = await fetch(`${apiBaseUrl()}/api/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data));
  return data;
}

/** @param {{ email: string, password: string }} creds */
export async function apiRegister({ email, password }) {
  const res = await fetch(`${apiBaseUrl()}/api/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data));
  return data;
}

/** Invite preview — no JWT; camelCase `{ valid, emailHint?, role?, expiresAtUtc?, existingAccount? }`. */
export async function apiGetInvitePreview(token) {
  const qs = new URLSearchParams({
    token: token != null ? String(token) : '',
  }).toString();
  const res = await fetch(`${apiBaseUrl()}/api/auth/invite-info?${qs}`, {
    headers: { Accept: 'application/json' },
  });
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data));
  return data;
}

// ── SUPER ADMIN ──────────────────────────────────────────────────────────────

/** Lists all users with the Admin role. */
export async function apiSuperAdminListAdmins() {
  const res = await authFetch('/api/super-admin/users');
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data));
  return Array.isArray(data) ? data : [];
}

/** Creates a new Admin user. @param {{ email: string, password: string }} body */
export async function apiSuperAdminCreateAdmin(body) {
  const res = await authFetch('/api/super-admin/users', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data));
  return data;
}

/** Deletes an Admin user by id. */
export async function apiSuperAdminDeleteAdmin(id) {
  const res = await authFetch(`/api/super-admin/users/${id}`, { method: 'DELETE' });
  if (res.status === 204) return;
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data));
}

/**
 * Impersonates an Admin user — returns their JWT.
 * @returns {Promise<{ token: string, expiresUtc: string, email: string, userId: string }>}
 */
export async function apiSuperAdminImpersonate(id) {
  const res = await authFetch(`/api/super-admin/users/${id}/impersonate`, { method: 'POST' });
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data));
  return data;
}

// ── CONTACT MESSAGES ─────────────────────────────────────────────────────────

/** Public — no auth. Submit a contact message. */
export async function apiSubmitContactMessage(body) {
  const res = await fetch(`${apiBaseUrl()}/api/contact`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data));
  return data;
}

/** SuperAdmin — list all contact messages. */
export async function apiContactListMessages() {
  const res = await authFetch('/api/super-admin/contact-messages/');
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data));
  return Array.isArray(data) ? data : [];
}

/** SuperAdmin — mark a message as read. */
export async function apiContactMarkRead(id) {
  const res = await authFetch(`/api/super-admin/contact-messages/${id}/read`, { method: 'PATCH' });
  if (res.status === 204) return;
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data));
}

/** SuperAdmin — delete a contact message. */
export async function apiContactDeleteMessage(id) {
  const res = await authFetch(`/api/super-admin/contact-messages/${id}`, { method: 'DELETE' });
  if (res.status === 204) return;
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data));
}

/** @param {{ token: string, password: string }} body */
export async function apiAcceptInvite({ token, password }) {
  const res = await fetch(`${apiBaseUrl()}/api/auth/accept-invite`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ token, password }),
  });
  const data = await parseBody(res);
  if (!res.ok) throw new Error(formatApiError(data));
  return data;
}
